"use client";

import { Consultation } from "@/app/_components/consultation-card";
import { Error } from "@/app/_components/error";
import { Loading } from "../loading";
import { SearchLink } from "../search-link";
import { ExOwnerInfo } from "../ex-owner-info";

import { useAnalyze } from "@/queries/use-analyze";

import { banks } from "@/constants";

import states from "@/static/states.json";

import type { DiligenceItem } from "../diligence.types";

const urls = {
  escavador: {
    url: "https://www.escavador.com/busca?q={query}",
    label: "escavador.com",
    icon: "/logos/escavador-logo.svg",
  },
  jusbrasil: {
    url: "https://www.jusbrasil.com.br/busca?q={query}",
    label: "jusbrasil.com.br",
    icon: "/logos/jusbrasil-logo.svg",
  },
  processorapido: {
    url: "https://processorapido.com",
    label: "processorapido.com",
    icon: "/logos/processorapido-logo.svg",
  },
};

const createSearchUrl = (baseUrl: string, query?: string) => {
  return baseUrl.replace("{query}", encodeURIComponent(query || ""));
};

export const Processos = ({ id, field, emptyElement }: DiligenceItem) => {
  const { data, isPending, error, refetch } = useAnalyze({ id, field });

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  const analyzeData = data?.analise?.data;
  const property = data?.propriedade;
  const hasValidData = analyzeData && property;

  if (!hasValidData) {
    return emptyElement ?? <p>Não há dados válidos para exibir.</p>;
  }

  const uf = property.uf || "";

  const state = states.find((state) => state.value === uf)!;

  const currentOwner =
    analyzeData.moradores && analyzeData.moradores.length > 0
      ? analyzeData.moradores[0]
      : null;

  const ownerName = currentOwner?.nome || "Não informado";
  const ownerCpf = currentOwner?.cpf || "Não informado";
  const inscricaoImobiliaria = property?.inscricao_imobiliaria || "";

  return (
    <div className="flex flex-col gap-8">
      <ExOwnerInfo
        currentOwner={currentOwner}
        analyzeData={analyzeData}
        propertyNumber={inscricaoImobiliaria}
      />
      <Consultation.Card
        title="Agregadores"
        description="Ler processos que envolvam o ex-proprietário pode dar indícios fortes. Por exemplo, um processo contra o credor pode indicar tentativa deanulação do leilão, um processo a favor do condomínio pode indicar a existência de débitos condominiais, etc."
      >
        <p className="text-sm text-muted-foreground">
          Para isso, realize uma consulta em um site agregador por meio do CPF.
        </p>
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Busca por Nome:
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <SearchLink
              url={createSearchUrl(urls.escavador.url, ownerName)}
              label={urls.escavador.label}
              icon={urls.escavador.icon}
            />
            <SearchLink
              url={createSearchUrl(urls.jusbrasil.url, ownerName)}
              label={urls.jusbrasil.label}
              icon={urls.jusbrasil.icon}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Busca por CPF:
          </h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchLink
              url={createSearchUrl(urls.escavador.url, ownerCpf)}
              label={urls.escavador.label}
              icon={urls.escavador.icon}
            />
            <SearchLink
              url={createSearchUrl(urls.jusbrasil.url, ownerCpf)}
              label={urls.jusbrasil.label}
              icon={urls.jusbrasil.icon}
            />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Com os processos em mãos, veja se há algo que valha a pena baixar e
          ler. Se sim, faça o download do processo na íntegra e o leia de trás
          pra frente.
        </p>
        <SearchLink
          url={createSearchUrl(urls.processorapido.url)}
          label={urls.processorapido.label}
          icon={urls.processorapido.icon}
        />
      </Consultation.Card>
      <Consultation.Card
        title="Tribunais"
        description={`Quer consultar direto na fonte? Entre no TJ de ${state.label} e busque por "Consulta Processual".`}
      >
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Consulta em Tribunais de Justiça Estaduais:
          </h2>
          <SearchLink url={state.tj} label={`Consultar TJ ${state.label}`} />
        </div>
        <div className="flex items-center gap-4">
          <div>
            <img src={banks.caixa.logo} className="w-16 h-16" />
          </div>
          <p className="text-sm text-muted-foreground">
            Para imóveis CAIXA, a consulta deve ser feita no TRF correspondente
            à região na qual o imóvel se encontra. Para MG, o TRF correto é o 6.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Consulta em Tribunais Regionais Federais:
          </h2>
          <SearchLink url={state.trf} label={`Consultar TRF ${state.label}`} />
        </div>
      </Consultation.Card>
    </div>
  );
};
